import { useQuery, UseQueryResult } from '@tanstack/react-query'

import Api from 'services/Api'
import { hashTable } from 'services/Utilities'

import { CACHE_TIME, RESSOURCES_ENDPOINTS, STALE_TIME } from 'Api/constants'
import { Config } from 'Api/types'
import { queryClient } from 'services/QueryClient'

interface ConfigResult {
  result: Config[]
  count: number
}

type Options = {
  page?: number
  limit?: number
  filter?: Record<string, unknown>
  sortBy?: string
}

const configsQuery = async (options?: Options) => {
  const configs: ConfigResult = await Api.get(RESSOURCES_ENDPOINTS.configs, {
    params: { ...options?.filter, limit: options?.limit, page: options?.page, sort: options?.sortBy, useNewReturn: true }
  })
  return configsTransformer(configs.result, configs.count)
}

const configsTransformer = (configs: Config[], count: number) =>
  ({
    configs,
    configsIds: configs?.map(({ _id }) => _id),
    configsById: configs?.length ? hashTable(configs) : undefined,
    count: count ?? 0
  } as const)

type ConfigsData = ReturnType<typeof configsTransformer>

const getQueryConfig = (options?: Options) =>
  ({
    queryKey: ['configs', { ...options }],
    queryFn: () => configsQuery(options),
    cacheTime: CACHE_TIME,
    staleTime: STALE_TIME
  } as const)

type Select<T> = (data: ConfigsData) => T

const useConfigs = <T = ConfigsData>(select?: Select<T>, options?: Options) => {
  const config = getQueryConfig(options)
  return useQuery({ ...config, select })
}

const useConfigsById = (id: string): UseQueryResult<Config | undefined> => {
  return useConfigs(data => data.configsById?.[id])
}

const prefetchConfigs = async () => queryClient.prefetchQuery({ ...getQueryConfig() })
const invalidateConfigs = async () => queryClient.invalidateQueries(getQueryConfig().queryKey)
const getConfigsQueryCache = () => queryClient.getQueryData(getQueryConfig().queryKey)

export { useConfigs, useConfigsById, prefetchConfigs, invalidateConfigs, getConfigsQueryCache }
